import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InternalPreparation } from '@haccp/api-client';
import { useToast } from '@/contexts/ToastContext';
import { useApiClient } from '@/hooks/useApiClient';

interface CreateBatchFormProps {
  preparations: InternalPreparation[];
  onSuccess?: () => void;
}

export default function CreateBatchForm({ preparations, onSuccess }: CreateBatchFormProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  const [formData, setFormData] = useState({
    internalPreparationId: '',
    quantity: 1,
    unit: 'kg',
    productionDateTime: new Date().toISOString().slice(0, 16), // Format: 2026-02-19T23:45
  });

  // Update production date/time to current when component mounts or when user opens it
  useEffect(() => {
    const updateDateTime = () => {
      const now = new Date();
      setFormData((prev) => ({
        ...prev,
        productionDateTime: now.toISOString().slice(0, 16),
      }));
    };

    // Update immediately
    updateDateTime();

    // Update every minute to keep current
    const interval = setInterval(updateDateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.createBatch({
        ...formData,
        productionDateTime: new Date(formData.productionDateTime).toISOString(),
      }),
    onSuccess: (batch) => {
      toast.addToast(t('dlc.batchCreated', { id: batch.batchId }), 'success');
      queryClient.invalidateQueries({ queryKey: ['preparation-batches'] });
      setFormData({
        internalPreparationId: '',
        quantity: 1,
        unit: 'kg',
        productionDateTime: new Date().toISOString().slice(0, 16),
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast.addToast(error.message || t('dlc.batchCreateError'), 'error');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.internalPreparationId || formData.quantity <= 0) {
      toast.addToast(t('dlc.fillAllFieldsCorrectly'), 'error');
      return;
    }
    createMutation.mutate();
  };

  const selectedPrep = preparations.find((p) => p.id === formData.internalPreparationId);

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
      <h3 className="text-lg font-bold text-gray-900 mb-4">
        {t('dlc.createBatchTitle')}
      </h3>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
        <p className="font-semibold">💡 {t('dlc.importantNote')}</p>
        <p>{t('dlc.productionDateAuto')}</p>
        <p>{t('dlc.dlcAutoCalculated')}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dlc.preparationLabel')} *
        </label>
        <select
          value={formData.internalPreparationId}
          onChange={(e) =>
            setFormData({ ...formData, internalPreparationId: e.target.value })
          }
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('dlc.selectPreparation')}</option>
          {preparations.map((prep) => (
            <option key={prep.id} value={prep.id}>
              {prep.name} - {t('dlc.duration')}: {prep.shelfLifeDays}{t('dlc.daysShort')} {prep.shelfLifeHours}{t('dlc.hoursShort')}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dlc.quantityLabel')} *
          </label>
          <input
            type="number"
            min="0"
            step="any"
            value={formData.quantity}
            onChange={(e) =>
              setFormData({ ...formData, quantity: parseFloat(e.target.value) || 1 })
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('dlc.unitLabel')}
          </label>
          <select
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="kg">kg</option>
            <option value="g">g</option>
            <option value="L">L</option>
            <option value="mL">mL</option>
            <option value="portions">portions</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('dlc.productionDateLabel')}
        </label>
        <input
          type="datetime-local"
          value={formData.productionDateTime}
          readOnly
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
        />
        <p className="text-xs text-gray-500 mt-1">
          {t('dlc.autoUpdated')}
        </p>
      </div>

      {selectedPrep && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-sm text-amber-800">
            <span className="font-semibold">{t('dlc.estimatedDlc')}:</span>
            <br />
            {new Date(
              new Date().getTime() +
                (selectedPrep.shelfLifeDays * 24 + selectedPrep.shelfLifeHours) * 60 * 60 * 1000
            ).toLocaleString(i18n.language)}
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={createMutation.isPending}
        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition"
      >
        {createMutation.isPending ? t('dlc.creating') : t('dlc.createBatchBtn')}
      </button>
    </form>
  );
}
